import { useId, useMemo } from "react";
import type { ItemPriceHistory } from "../../api/types";

interface PriceHistoryChartProps {
  itemName: string;
  points: ItemPriceHistory["points"];
}

/** viewBox units. The SVG scales to its container; these are not pixels. */
const W = 640;
const H = 260;
const PAD = { top: 16, right: 18, bottom: 34, left: 56 };
const PLOT_W = W - PAD.left - PAD.right;
const PLOT_H = H - PAD.top - PAD.bottom;

const eur = (value: number) =>
  value.toLocaleString(undefined, {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const shortDate = (iso: string) =>
  new Date(iso).toLocaleDateString([], { day: "numeric", month: "short" });

const longDate = (iso: string) =>
  new Date(iso).toLocaleDateString([], {
    day: "numeric",
    month: "short",
    year: "numeric",
  });

interface Series {
  key: "quoted" | "boxOpen";
  slot: 1 | 4;
  label: string;
  /** Aligned with `points`; null where that batch has no value for the series. */
  values: Array<number | null>;
}

/** Rounds a raw axis span up to a readable step (1, 2 or 5 × 10^n). */
function niceStep(span: number) {
  const raw = span / 4;
  const magnitude = 10 ** Math.floor(Math.log10(raw));
  const normalised = raw / magnitude;
  const factor = normalised <= 1 ? 1 : normalised <= 2 ? 2 : normalised <= 5 ? 5 : 10;
  return factor * magnitude;
}

/**
 * G-26 (owner request 2026-07-31): purchase price per batch over time. Two
 * series share one axis — what the supplier QUOTED per unit, and what the unit
 * really cost once receiving damage was deducted (box-open). The distance
 * between the lines is the supplier-honesty story this page exists to tell.
 *
 * Colors are the validated `--chart-N` tokens, so the chart re-themes with the
 * app; identity is never color-alone — the legend spells both series out and
 * the table below the chart carries every number.
 */
export function PriceHistoryChart({ itemName, points }: PriceHistoryChartProps) {
  const titleId = useId();
  const descId = useId();

  const series = useMemo<Series[]>(
    () => [
      {
        key: "quoted",
        slot: 1,
        label: "Quoted €/unit",
        values: points.map((p) =>
          p.quotedUnitPriceEur != null ? Number(p.quotedUnitPriceEur) : null,
        ),
      },
      {
        key: "boxOpen",
        slot: 4,
        label: "Box-open €/unit",
        values: points.map((p) => Number(p.unitCostEur)),
      },
    ],
    [points],
  );

  const geometry = useMemo(() => {
    const times = points.map((p) => new Date(p.receivedAt).getTime());
    const values = series
      .flatMap((s) => s.values)
      .filter((v): v is number => v != null && Number.isFinite(v));
    if (values.length === 0) return null;

    const minValue = Math.min(...values);
    const maxValue = Math.max(...values);
    // A flat series would collapse to a zero-height axis; give it room so the
    // line reads as "steady" rather than as a rendering failure.
    const span = maxValue - minValue || Math.max(maxValue, 1) * 0.2;
    const step = niceStep(span);
    // Pad both ends before snapping to the step, so a value that happens to be
    // an exact multiple does not land on the axis line itself.
    const axisMin = Math.max(0, Math.floor((minValue - span * 0.08) / step) * step);
    const axisMax = Math.ceil((maxValue + span * 0.08) / step) * step;
    const axisSpan = axisMax - axisMin || 1;

    const minTime = Math.min(...times);
    const maxTime = Math.max(...times);
    const timeSpan = maxTime - minTime;

    const x = (index: number) =>
      // One point, or several batches received the same day: centre them.
      timeSpan === 0
        ? PAD.left + PLOT_W / 2
        : PAD.left + ((times[index] - minTime) / timeSpan) * PLOT_W;
    const y = (value: number) =>
      PAD.top + PLOT_H - ((value - axisMin) / axisSpan) * PLOT_H;

    const ticks: number[] = [];
    for (let v = axisMin; v <= axisMax + step / 2; v += step) ticks.push(v);

    return { x, y, ticks, minValue, maxValue };
  }, [points, series]);

  if (!geometry) return null;
  const { x, y, ticks, minValue, maxValue } = geometry;

  const first = points[0];
  const last = points[points.length - 1];
  const sameDay = shortDate(first.receivedAt) === shortDate(last.receivedAt);
  // When nothing arrived damaged the two series carry identical numbers and
  // draw on top of each other. Saying so beats letting the reader wonder
  // which line is missing.
  // Only worth saying once there are lines to confuse; the single-batch case
  // has its own note in the panel.
  const seriesCoincide =
    points.length > 1 &&
    points.every(
      (p) =>
        p.quotedUnitPriceEur != null &&
        Number(p.quotedUnitPriceEur) === Number(p.unitCostEur),
    );
  const summary =
    points.length === 1
      ? `One batch of ${itemName} on ${longDate(first.receivedAt)}.`
      : `${points.length} batches of ${itemName} between ${longDate(first.receivedAt)} and ${longDate(last.receivedAt)}. Unit price ranged from ${eur(minValue)} to ${eur(maxValue)}.`;

  return (
    <figure className="price-chart">
      <svg
        className="price-chart-svg"
        viewBox={`0 0 ${W} ${H}`}
        role="img"
        aria-labelledby={`${titleId} ${descId}`}
      >
        <title id={titleId}>{`Price per unit over time — ${itemName}`}</title>
        <desc id={descId}>{summary}</desc>

        {ticks.map((tick) => (
          <g key={tick}>
            <line
              className="price-chart-grid"
              x1={PAD.left}
              x2={W - PAD.right}
              y1={y(tick)}
              y2={y(tick)}
            />
            <text className="price-chart-axis" x={PAD.left - 8} y={y(tick) + 4}>
              {eur(tick)}
            </text>
          </g>
        ))}

        {series.map((s) => {
          // Break the line wherever a batch has no value for that series, so a
          // missing invoice total never draws a straight line across the gap.
          const runs: Array<Array<{ x: number; y: number }>> = [];
          let run: Array<{ x: number; y: number }> = [];
          s.values.forEach((value, index) => {
            if (value == null || !Number.isFinite(value)) {
              if (run.length > 0) runs.push(run);
              run = [];
              return;
            }
            run.push({ x: x(index), y: y(value) });
          });
          if (run.length > 0) runs.push(run);

          return (
            <g key={s.key} data-slot={s.slot}>
              {runs.map((segment, i) =>
                segment.length > 1 ? (
                  <polyline
                    key={i}
                    className="price-chart-line"
                    points={segment.map((p) => `${p.x},${p.y}`).join(" ")}
                  />
                ) : null,
              )}
              {s.values.map((value, index) =>
                value != null && Number.isFinite(value) ? (
                  <circle
                    key={points[index].batchId}
                    className="price-chart-dot"
                    cx={x(index)}
                    cy={y(value)}
                    // Box-open sits on a smaller radius so that an undamaged
                    // delivery — where both series carry the same number and
                    // the markers land on the same pixel — still reads as two
                    // dots: a box-open core inside a quoted ring.
                    r={s.key === "quoted" ? 5.5 : 3}
                  >
                    <title>{`${s.label} — ${eur(value)} on ${longDate(points[index].receivedAt)}${
                      points[index].supplierName ? ` · ${points[index].supplierName}` : ""
                    }`}</title>
                  </circle>
                ) : null,
              )}
            </g>
          );
        })}

        <line
          className="price-chart-axis-line"
          x1={PAD.left}
          x2={W - PAD.right}
          y1={PAD.top + PLOT_H}
          y2={PAD.top + PLOT_H}
        />
        {sameDay ? (
          // Repeating "Jun 28" at both ends of the axis reads as a rendering
          // bug; one centred label is the honest version.
          <text
            className="price-chart-axis price-chart-axis-middle"
            x={PAD.left + PLOT_W / 2}
            y={H - 10}
          >
            {shortDate(first.receivedAt)}
          </text>
        ) : (
          <>
            <text
              className="price-chart-axis price-chart-axis-start"
              x={PAD.left}
              y={H - 10}
            >
              {shortDate(first.receivedAt)}
            </text>
            <text className="price-chart-axis" x={W - PAD.right} y={H - 10}>
              {shortDate(last.receivedAt)}
            </text>
          </>
        )}
      </svg>

      <figcaption className="price-chart-legend">
        {series.map((s) => (
          <span key={s.key}>
            <span className="price-chart-swatch" data-slot={s.slot} aria-hidden="true" />
            {s.label}
          </span>
        ))}
        {seriesCoincide ? (
          <span className="price-chart-coincide">
            Nothing arrived damaged — both lines carry the same price.
          </span>
        ) : null}
      </figcaption>
    </figure>
  );
}
